import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  ShimmerButton,
  Input,
  Label,
  Alert,
  AlertDescription,
} from '@dymaxion/ui';
import {
  useLogin,
  useSessionStore,
  useRuntimeConfig,
  generateDeviceFingerprint,
  isValidDeviceFingerprint,
  getTenantFromHostname,
} from '@dymaxion/auth';

/**
 * LoginPage — DYMAXION glass-on-dark login surface.
 *
 * Visual language sourced from the 21st.dev Paper Design Shader
 * Showcase template Karim approved as the new common style guide
 * (SCRUM-721, 2026-04-13). Black base, animated cyan/orange mesh
 * gradient backdrop, glass-on-dark form card, gradient-text wordmark.
 *
 * Architectural fix for Stage 5 "layout + buttons messed up": the
 * hero is a regular React component in the same tree as the form.
 */
/**
 * AWSDO-1328 — Robust return_url extraction.
 *
 * The spoke nginx auth gates now pass `?return_url=$scheme://$host$request_uri`
 * (the full absolute URL of the originally-requested page, preserving the
 * source subdomain). Two consumption concerns:
 *
 *   1. URLSearchParams handles embedded `?` inside a value correctly (only
 *      splits on `&`), so for return URLs like
 *      `https://monitoring-archon-tpg1.dymaxion.com.au/grafana/d/foo?orgId=1`
 *      the standard `.get('return_url')` works as-is.
 *
 *   2. If the original URL contains `&` (e.g. multiple Grafana query
 *      params), URLSearchParams would split on that and truncate the value.
 *      To survive, fall back to a longest-prefix match from the raw search
 *      string, on the assumption that `return_url` is the LAST query param
 *      (nginx ensures this).
 */
function extractReturnUrl(search: string): string | null {
  const params = new URLSearchParams(search);
  const standard = params.get('return_url') ?? params.get('next');
  if (!standard) return null;

  // If standard already looks like a complete absolute URL with no `&`
  // truncation risk, return it.
  if (!standard.startsWith('http://') && !standard.startsWith('https://')) {
    return standard;
  }

  // Absolute URL path: check if the raw search string suggests `&` was
  // consumed. Recover by taking everything after `return_url=` (case
  // insensitive `next` not handled here — relies on standard param).
  const cleaned = search.startsWith('?') ? search.slice(1) : search;
  const idx = cleaned.indexOf('return_url=');
  if (idx === -1) return standard;
  const raw = cleaned.substring(idx + 'return_url='.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return standard;
  }
}

export function LoginPage() {
  const id = useId();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Accept either ?return_url= (legacy /login form parameter, also
  // used by dymaxion-login-service) OR ?next= (used by the outer
  // nginx error_page 401 redirects + Lua auth blocks for grafana,
  // prometheus, mailhog). SCRUM-728 / SCRUM-729: the auth gate now
  // bounces unauthenticated users back to /login-v2/ instead of the
  // legacy /login form, so the SPA must accept both query-param
  // names to preserve the 'redirect back to original URL after auth'
  // UX flow on every JWT-gated route across the platform.
  //
  // AWSDO-1328: return_url may now be an ABSOLUTE URL within
  // .dymaxion.com.au (cross-subdomain auth — e.g. session expires on
  // monitoring-archon-tpg1.dymaxion.com.au, user redirected to
  // archon-tpg1.dymaxion.com.au/login-v2/, must return to original
  // subdomain post-login). Backend validateReturnUrl accepts the same
  // parent allowlist domain. See extractReturnUrl() above.
  const returnUrlParam = extractReturnUrl(window.location.search);
  // Suppress unused-import lint warning if searchParams becomes
  // unreferenced after this refactor — searchParams is still used
  // elsewhere in this component if needed.
  void searchParams;
  const config = useRuntimeConfig();
  const { brand, auth: authConfig } = config.dymaxionFrontend;

  // Organisation is config-derived (per-host), not user-picked. The
  // hostname-detection path is the primary source; the configured
  // defaultOrganization is the fallback. It is still SENT in the login
  // payload, but the user never chooses it. Role is no longer sent at
  // all — the backend derives it from the user's Authentik LDAP groups.
  const organization = useMemo(
    () =>
      getTenantFromHostname(undefined, authConfig.knownTenants) ??
      authConfig.defaultOrganization,
    [authConfig.knownTenants, authConfig.defaultOrganization],
  );

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [fingerprint, setFingerprint] = useState<string>('');
  const [fingerprintError, setFingerprintError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState<{
    userName: string;
    redirectUrl: string;
  } | null>(null);
  // AWSDO-1286: device-rebind state. When the spoke returns 403 with a
  // device-binding error code (device_inactive / device_revoked /
  // device_not_registered), the SPA must render an explicit, clickable
  // CTA to the Hub change-machine flow — not rely on a silent
  // window.location.href timeout. Karim hit this on TPG 2026-05-12:
  // backend returned the correct details.redirect_url but the SPA
  // matched the error only via free-text "device inactive" includes()
  // check, which failed against the new wording "This device is no
  // longer active…". This card mirrors the loginSuccess card structure
  // so visual language stays consistent.
  const [rebindRedirect, setRebindRedirect] = useState<{
    url: string;
    message: string;
  } | null>(null);

  // True when the page is served over plain HTTP on a non-localhost
  // origin. `window.crypto.subtle` is only exposed in a SecureContext
  // (HTTPS or localhost), so device fingerprinting cannot run. We
  // show a subtle info note instead of a destructive error alert.
  const isInsecureContext =
    typeof window !== 'undefined' && window.isSecureContext === false;

  const login = useLogin();
  const setSession = useSessionStore((s) => s.setSession);

  useEffect(() => {
    // Skip fingerprint generation entirely in insecure contexts —
    // calling crypto.subtle.digest would throw immediately.
    if (isInsecureContext) return;
    // Also skip if the host config disables fingerprinting (rare,
    // useful for tenants that opt out of the three-factor scheme).
    if (!authConfig.deviceFingerprintingRequired) return;

    let cancelled = false;
    void (async () => {
      try {
        const fp = await generateDeviceFingerprint();
        if (cancelled) return;
        if (!isValidDeviceFingerprint(fp)) {
          setFingerprintError(
            'Could not generate a device fingerprint. Three-factor authentication requires a supported browser environment (SubtleCrypto API).',
          );
        }
        setFingerprint(fp);
      } catch (err) {
        if (cancelled) return;
        setFingerprintError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInsecureContext, authConfig.deviceFingerprintingRequired]);

  // canSubmit only gates on visible form fields + pending state.
  // Fingerprint validity is checked at submit time so the button stays
  // clickable and gives a clear error message in insecure contexts
  // (plain-HTTP public-IP review URLs) instead of mysteriously greying
  // out.
  const canSubmit =
    username.length > 0 &&
    password.length > 0 &&
    !login.isPending;

  // AWSDO-1286: helper that decides whether a 403/failure response is a
  // device-rebind condition. Matches BOTH the canonical error_code
  // (preferred) AND legacy free-text message substrings (defence-in-
  // depth in case the backend tweaks the human-readable message
  // again). The 2026-05-12 production trip-up was that the new
  // backend message ("This device is no longer active…") did NOT
  // contain the substring "device inactive" the SPA was looking for,
  // and error_code only matched device_not_registered, so the inactive
  // and revoked codes fell through silently.
  const isDeviceRebindError = (
    errorCode: string,
    errorMsg: string,
  ): boolean => {
    if (
      errorCode === 'device_not_registered' ||
      errorCode === 'device_inactive' ||
      errorCode === 'device_revoked'
    ) {
      return true;
    }
    const msg = errorMsg.toLowerCase();
    return (
      msg.includes('device not registered') ||
      msg.includes('device revoked') ||
      msg.includes('device inactive') ||
      msg.includes('no longer active') ||
      msg.includes('re-register your device')
    );
  };

  // AWSDO-1286: build the full Hub rebind URL including the device
  // fingerprint and a spoke return URL so the change-machine flow can
  // bounce the user back here after a successful rebind.
  const buildRebindUrl = (hubBase: string): string => {
    const trimmed = hubBase || 'https://archon-hub1-aws.dymaxion.com.au/register/user/change-machine';
    const spokeReturn = encodeURIComponent(window.location.origin + '/login-v2/');
    const fp = encodeURIComponent(fingerprint);
    return `${trimmed}?fp=${fp}&spoke=${spokeReturn}`;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    setRebindRedirect(null);

    if (!canSubmit) return;

    // Insecure-context guard — the Sign-in button is now clickable so
    // the user gets an explicit error explaining why, rather than a
    // silently-disabled button.
    if (isInsecureContext) {
      setSubmitError(
        'Sign-in is unavailable on this URL. DYMAXION three-factor ' +
          'authentication requires a secure context (HTTPS or localhost). ' +
          'Visit the production URL at https://archon.dymaxion.com.au/ ' +
          'or use an SSH tunnel for local review.',
      );
      return;
    }

    if (authConfig.deviceFingerprintingRequired && !isValidDeviceFingerprint(fingerprint)) {
      setSubmitError(
        'Could not generate a device fingerprint. Refresh the page and ' +
          'try again, or check your browser supports the SubtleCrypto API.',
      );
      return;
    }

    try {
      const result = await login.mutateAsync({
        organization,
        username,
        password,
        return_url: returnUrlParam,
        deviceFingerprint: fingerprint,
      });

      if (result.success) {
        // AWSDO-1188: pass user + redirectUrl + expiresIn (the server's
        // canonical fields) instead of trying to decode the JWT from
        // the JSON body — the raw token lives only in the httpOnly
        // cookie. See packages/auth/src/session.ts.
        setSession(result.user, result.redirectUrl, result.expiresIn);
        // Persist local success state so React-router state is consistent
        // if navigation is interrupted, then route to /post-auth where
        // the PostAuthRedirectPage component fires the existing 600 ms
        // setTimeout(window.location.href = redirectUrl) auto-redirect.
        // AWSDO-1359: the previous build stopped here and rendered a
        // "Continue to workspace →" confirmation card, which users
        // perceived as an SSO loop because the explicit click step was
        // not announced. AWSDO-1357 diagnosis confirmed nginx + backend
        // + cookie scoping were all already correct; the only missing
        // piece was this navigate() into the existing /post-auth route.
        setLoginSuccess({
          userName: result.user.displayName || result.user.username,
          redirectUrl: result.redirectUrl,
        });
        navigate('/post-auth');
        return;
      }

      // AWSDO-1286: success-path failure branch. AuthApiError thrown on
      // 403 is handled in the catch below — this only fires when the
      // server returns 200 with success:false (rare; older test paths).
      const details = result.details && !Array.isArray(result.details) ? result.details : null;
      const errorCode = details?.error_code?.toLowerCase() ?? '';
      const errorMsg = (result.message || result.error || '').toLowerCase();

      if (isDeviceRebindError(errorCode, errorMsg)) {
        const redirectUrl = buildRebindUrl(details?.redirect_url ?? '');
        const message =
          result.message ||
          'Your device is no longer active. Click the button below to re-register your device via the Hub.';
        setRebindRedirect({ url: redirectUrl, message });
        return;
      }

      setSubmitError(result.message || result.error || 'Login failed. Check your credentials.');
    } catch (err: unknown) {
      // AWSDO-1286: 403/4xx path. jsonRequest() throws AuthApiError with
      // payload = the parsed JSON body. The spoke shape is:
      //   { success:false, error, message, details:{ error_code, redirect_url } }
      // We surface a dedicated rebind card with a clickable CTA when
      // the error code is one of the three device-binding variants.
      const payload = (err as {
        payload?: {
          error?: string;
          message?: string;
          details?: { error_code?: string; redirect_url?: string };
        };
      }).payload;
      const errorCode = payload?.details?.error_code?.toLowerCase() ?? '';
      const errorMsg = (payload?.message || payload?.error || '').toLowerCase();

      if (isDeviceRebindError(errorCode, errorMsg)) {
        const redirectUrl = buildRebindUrl(payload?.details?.redirect_url ?? '');
        const message =
          payload?.message ||
          'Your device is no longer active. Click the button below to re-register your device via the Hub.';
        setRebindRedirect({ url: redirectUrl, message });
        return;
      }

      setSubmitError(
        payload?.message || (err instanceof Error ? err.message : 'Network error. Please try again.'),
      );
    }
  };

  return (
    <div className="w-full max-w-[460px]">
      {/*
       * Wordmark — single-word "DYMAXION" all caps in the same typography
       * as the Spline scene's "for business" text. IBM Plex Sans
       * extralight (200) with wide tracking is a close default; tune
       * once Karim confirms the exact Spline font/colour values.
       */}
      <motion.header
        className="mb-8 text-left"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {/*
         * Brand preamble — small caps, low-emphasis, from runtime config.
         * Empty string in config hides the line entirely.
         */}
        {brand.preamble && (
          <p
            className="mb-3 text-base font-medium uppercase text-white/55"
            style={{ letterSpacing: '0.32em' }}
          >
            {brand.preamble}
          </p>
        )}
        {/*
         * Wordmark — gradient sampled from the Spline scene's "for
         * business" text. Rendered via background-clip: text so the
         * glyph outlines pick up the gradient without tinting the
         * rest of the card. Wordmark text comes from runtime config.
         */}
        <h1
          className="font-extralight leading-none"
          style={{
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            letterSpacing: '0.18em',
            backgroundImage:
              'linear-gradient(100deg, #04776A 0%, #0BB5BA 45%, #5FE8EC 75%, #A8F5F8 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {brand.wordmark}
        </h1>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#0BB5BA]/40 bg-[#0BB5BA]/10 px-4 py-1.5 text-xs font-medium text-white/80 backdrop-blur-sm">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0BB5BA]" />
          {brand.tagline}
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.3 }}
      >
        {loginSuccess ? (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-white">Signed in</CardTitle>
              <CardDescription className="text-white/60">
                Welcome back, {loginSuccess.userName}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-[#0BB5BA]/40 bg-[#0BB5BA]/10 px-4 py-3 text-sm text-white/80 backdrop-blur-sm">
                <div className="mb-1 font-medium text-white">Authentication successful.</div>
                <div className="text-xs text-white/60">
                  Your JWT session is active. Production would redirect to:
                </div>
                <div className="mt-2 break-all rounded border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-[#5FE8EC]">
                  {loginSuccess.redirectUrl}
                </div>
              </div>
              {/*
                * Primary post-login CTA — ShimmerButton in the success
                * variant (DYMAXION teal #0BB5BA gradient with #5FE8EC
                * highlight stop sweeping across at 2 s/cycle). The
                * activation-state colour change is obvious because the
                * Sign in button above was purple — clicking through the
                * success card visibly flips the brand colour.
                */}
              <ShimmerButton
                type="button"
                variant="success"
                className="w-full text-base"
                onClick={() => {
                  window.location.href = loginSuccess.redirectUrl;
                }}
              >
                Continue to workspace →
              </ShimmerButton>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => {
                  setLoginSuccess(null);
                  setUsername('');
                  setPassword('');
                }}
              >
                Sign in as a different user
              </Button>
            </CardContent>
          </Card>
        ) : rebindRedirect ? (
          // AWSDO-1286: device-rebind card. The spoke validated the
          // user's credentials successfully but the device fingerprint
          // is not active in the Hub license DB (deactivated,
          // revoked, or never registered from this device). We show an
          // explicit, prominent CTA that takes the user to the Hub
          // change-machine flow — replacing the previous silent
          // window.location.href timeout which left users staring at
          // an error message with no obvious next action.
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-white">Device re-registration required</CardTitle>
              <CardDescription className="text-white/60">
                We need to verify this device with the Hub before we can sign you in.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{rebindRedirect.message}</AlertDescription>
              </Alert>
              <div className="rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/5 px-4 py-3 text-sm text-white/80 backdrop-blur-sm">
                <div className="mb-1 font-medium text-white">Next step</div>
                <div className="text-xs text-white/60">
                  You will be taken to the AWS Hub to re-register your device fingerprint.
                  After completing the registration, you will be redirected back here to
                  sign in.
                </div>
                <div className="mt-2 break-all rounded border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-[#A847E8]">
                  {rebindRedirect.url}
                </div>
              </div>
              {/*
                * Primary CTA — ShimmerButton in the primary variant
                * (DYMAXION brand purple). The user MUST click this to
                * navigate to the Hub change-machine flow; we no longer
                * rely on a silent setTimeout redirect that could be
                * blocked by browser settings or missed by the user.
                */}
              <ShimmerButton
                type="button"
                variant="primary"
                className="w-full text-base"
                onClick={() => {
                  window.location.href = rebindRedirect.url;
                }}
              >
                Re-register Device on Hub →
              </ShimmerButton>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => {
                  setRebindRedirect(null);
                }}
              >
                Back to sign-in
              </Button>
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-white">Sign in to your workspace</CardTitle>
            <CardDescription className="text-white/60">
              Enter your credentials to sign in.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form className="space-y-5" onSubmit={onSubmit} noValidate>
              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor={`${id}-username`} className="text-white/90">
                  Username or email
                </Label>
                <Input
                  id={`${id}-username`}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor={`${id}-password`} className="text-white/90">
                  Password
                </Label>
                <Input
                  id={`${id}-password`}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {/*
               * Insecure-context notice — plain HTTP on a non-localhost
               * origin can't run SubtleCrypto, so the Sign-in button
               * stays disabled. We show a subtle info note explaining
               * why (instead of a red destructive alert that looks like
               * the page is broken).
               */}
              {isInsecureContext && (
                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-xs text-white/60 backdrop-blur-sm">
                  <span className="font-medium text-white/80">Review mode — </span>
                  full sign-in is only available over HTTPS or localhost. The production URL at
                  <span className="font-mono text-white/80"> archon.dymaxion.com.au</span> works
                  normally.
                </div>
              )}

              {/* Fingerprint-error alert only appears in secure contexts where
                  we actually expect it to work (e.g. browser-level failure). */}
              {!isInsecureContext && fingerprintError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{fingerprintError}</AlertDescription>
                </Alert>
              )}

              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              {/*
                * Primary form CTA — ShimmerButton in the primary variant
                * (DYMAXION brand purple #8200DB gradient with #A847E8
                * highlight stop sweeping across at 2 s/cycle). The
                * shimmer wash is a continuous affordance that says "I'm
                * the click target" without ambiguity, and the purple
                * fill is the brand identity. Activation flips to teal
                * via the "Continue to workspace" ShimmerButton above.
                */}
              <ShimmerButton
                type="submit"
                variant="primary"
                className="w-full text-base"
                disabled={!canSubmit}
              >
                {login.isPending ? 'Signing in…' : 'Sign in'}
              </ShimmerButton>
            </form>
          </CardContent>
        </Card>
        )}
      </motion.div>
    </div>
  );
}
